import {parse} from "query-string";
import * as React from "react";
import {Alert, Button, Col, FormControl, FormGroup, Grid} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {connect} from "react-redux";
import {Redirect, RouteComponentProps, withRouter} from "react-router-dom";
import {Fields, InjectedFormProps, reduxForm, SubmissionError} from "redux-form";
import {NavigationBar} from "../common/ui/Navbar";
import {Logger} from "../common/util/Logger";
import {MappEntConfigurationInfo} from "../model";
import * as paths from "../paths";
import {selectors as startupSelectors} from "../startup/selectors";
import {actions} from "./actions";
import {
  localRepository,
  PREVIEW_BACKGROUND_TYPE,
  PREVIEW_BACKGROUND_BOUNDS,
  PREVIEW_BACKGROUND_CRS,
  PREVIEW_BACKGROUND_URL,
  PREVIEW_BACKGROUND_SUBDOMAINS,
  PREVIEW_BACKGROUND_LAYERNAME,
  LAST_SILENT_RENEW
} from "../common/LocalRepository";
import {oidcProviderInfo} from "../oidc/startOidcProvider";
import {OidcLoginButton} from "../oidc/OidcLoginButton";
import {userManager} from "../oidc/utils/userManager";
import {Links} from "../help/model";
import {SilentRenewPage} from "../oidc/SilentRenewPage";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {oidcUtils} from "../oidc/utils/OidcUtils";

interface LoginFormData {
  username: string;
  password: string;
}

interface LoginPageProperties {
  dispatch: (action) => any;
  mappEntconfigurationInfo: MappEntConfigurationInfo;
}

const LOGIN_PAGE_MESSAGES = defineMessages({
  usernamePlaceholder: {
    id: "studio.login-page.username-placeholder",
    defaultMessage: "Username",
  },
  passwordPlaceholder: {
    id: "studio.login-page.password-placeholder",
    defaultMessage: "Password",
  },
});

export class LoginPagePresentation extends React.Component<LoginPageProperties & RouteComponentProps<any> & WithApiProperties & InjectedIntlProps & InjectedFormProps<LoginFormData, LoginPageProperties>, {}> {

  _logger: Logger = Logger.getLogger("Login");

  renderFields = (fields) => {
    const {intl} = this.props;

    return (
        <div>
          <FormGroup controlId={fields[LOGIN_FORM_USERNAME_FIELD_NAME].name}>
            <FormControl
                type="text"
                {...fields[LOGIN_FORM_USERNAME_FIELD_NAME].input}
                placeholder={intl.formatMessage(LOGIN_PAGE_MESSAGES.usernamePlaceholder)}
            />
          </FormGroup>
          <FormGroup controlId={fields[LOGIN_FORM_PASSWORD_FIELD_NAME].name}>
            <FormControl
                type="password"
                {...fields[LOGIN_FORM_PASSWORD_FIELD_NAME].input}
                placeholder={intl.formatMessage(LOGIN_PAGE_MESSAGES.passwordPlaceholder)}
            />
          </FormGroup>
        </div>
    );
  }

  submitFormValues = (values) => {
    const {dispatch, location, intl} = this.props;

    const queryObj = parse(location.search);
    const redirectTo = queryObj.redirect ? queryObj.redirect.toString() : "/";
    if (!oidcProviderInfo?.enabled) {
      return dispatch(actions.login({
        username: values[LOGIN_FORM_USERNAME_FIELD_NAME],
        password: values[LOGIN_FORM_PASSWORD_FIELD_NAME],
        redirect: paths.getPageURL() + "#" + redirectTo,
      }, intl)).then(() => {
        this.loadConfig(redirectTo);
      }).catch((error) => {
        throw new SubmissionError({_error: error});
      });
    }
  }

  configLoaded = (redirectTo) => {
    const {history} = this.props;
    history.push(redirectTo);
  }

  loadConfig = (redirectTo) => {
    this.props.api.getStudioSettings()
        .then((studioSettings) => {
          const {
            previewBackgroundType,
            previewBackgroundBounds,
            previewBackgroundCrs,
            previewBackgroundUrl,
            previewBackgroundLayername,
            previewBackgroundSubdomains
          } = studioSettings.data.values;
          localRepository.setParameter(PREVIEW_BACKGROUND_TYPE, previewBackgroundType);
          localRepository.setParameter(PREVIEW_BACKGROUND_BOUNDS, previewBackgroundBounds);
          localRepository.setParameter(PREVIEW_BACKGROUND_CRS, previewBackgroundCrs);
          localRepository.setParameter(PREVIEW_BACKGROUND_URL, previewBackgroundUrl);
          localRepository.setParameter(PREVIEW_BACKGROUND_SUBDOMAINS, previewBackgroundSubdomains);
          localRepository.setParameter(PREVIEW_BACKGROUND_LAYERNAME, previewBackgroundLayername);
        })
        .catch((error) => {
          this.setState({submitting: false, error});
        })
        .finally(() => {
          this.configLoaded(redirectTo);
        });
  }

  renderError = () => {
    const error = this.props.error as any; //redux-form typings state that error is a string. This is a lie.
    const isNetworkError = !error.response && error.message.includes("Network");
    const additionalErrorInfo = isNetworkError ? "Backend could not be reached. Is it offline?" : error.message;

    this._logger.error(error.message);

    return (
        <Alert bsStyle="danger">
          <FormattedMessage
              id="studio.login-page.login-failed"
              defaultMessage="Login failed!"
          /> {additionalErrorInfo}
        </Alert>);
  }

  renderLoginPage = () => {
    const {handleSubmit, submitting, submitFailed, location} = this.props;
    const isOidcEnabled = oidcProviderInfo?.enabled;
    const message = parse(location.search).message;
    const messageDisplay = message ? (
        <Alert bsStyle="info">
          {message}
        </Alert>) : null;
    localRepository.setParameter(LAST_SILENT_RENEW, null);
    return (
        <div data-cy="login-page">
          <NavigationBar isAuthenticated={false}/>
          <Grid fluid style={{padding: 0}}>
            <Col xs={12} style={{padding: 0}}>
              <div className="splash"/>
            </Col>
            <Col>
              <div style={{textAlign: "center"}}>
                <Button bsStyle="link" className="actionBarItem"
                        onClick={(_event) => window.open("../../catalogexplorer/", "_blank", "noreferrer")}>
                  <FormattedMessage
                      id="studio.login-page.catalog-explorer"
                      defaultMessage="Catalog Explorer"
                  />
                </Button>
                <Button bsStyle="link" className="actionBarItem"
                        onClick={(_event) => window.open(Links.USER_GUIDE, "_blank", "noreferrer")}>
                  <FormattedMessage
                      id="studio.login-page.user-guide"
                      defaultMessage="ERDAS Apollo User Guide"
                  />
                </Button>
                <Button bsStyle="link" className="actionBarItem"
                        onClick={(_event) => window.open(Links.SUPPORT, "_blank", "noreferrer")}>
                  <FormattedMessage
                      id="studio.login-page.support"
                      defaultMessage="Hexagon Support"
                  />
                </Button>
              </div>
            </Col>
            <Col xs={8} xsOffset={2} md={4} mdOffset={4}>
              <h3>
                <FormattedMessage
                    id="studio.login-page.header"
                    defaultMessage="LOGIN"
                />
              </h3>
              <form onSubmit={handleSubmit(this.submitFormValues)}>
                {messageDisplay}
                {submitFailed ? this.renderError() : null}
                {!isOidcEnabled &&
                 <Fields names={[LOGIN_FORM_USERNAME_FIELD_NAME, LOGIN_FORM_PASSWORD_FIELD_NAME,
                                 LOGIN_FORM_REDIRECT_FIELD_NAME]}
                         component={this.renderFields}/>}
                {!isOidcEnabled && <div style={{textAlign: "center"}}>
                  {submitting
                   ? (<p>
                        <FormattedMessage id="studio.login-page.loading"
                                          defaultMessage="Loading Page..."/>
                      </p>)
                   : <Button type="submit" bsStyle="primary">
                     <FormattedMessage id="studio.login-page.button" defaultMessage="LOGIN"/>
                   </Button>}
                </div>}
                {isOidcEnabled && <div style={{textAlign: "center"}}>
                  {submitting
                   ? (<p>
                        <FormattedMessage id="studio.login-page.loading" defaultMessage="Loading Page..."/>
                      </p>)
                   : <OidcLoginButton/>}
                </div>}
              </form>
            </Col>
          </Grid>
        </div>
    );
  }

  render() {
    const {location, mappEntconfigurationInfo} = this.props;
    const isOidcEnabled = oidcProviderInfo?.enabled;
    const sessionIsAvailable = oidcUtils.getOidcTokenValue() !== "";

    if (mappEntconfigurationInfo?.securityInfo?.isMappEnterprise) {
      return <Redirect to={{
        pathname: "/",
      }}/>;
    } else if (isOidcEnabled && sessionIsAvailable) {
      const data = {
        hash: location.hash.length > 0 ? location.hash : '#',
        urlBeforeRedirection: '/data'
      };
      if (!oidcUtils.isOidcAuthenticated()) {
        userManager.signinRedirect({data});
        return <Redirect to={{
          pathname: '#/data',
        }}/>;
      } else {
        const lastSilentRenew = localRepository.getParameter(LAST_SILENT_RENEW);
        const now = Date.now();
        //this prevents the login page from looping SilentRenew when MaxSessionTimeout has been reached
        if (!lastSilentRenew || now > parseInt(lastSilentRenew, 10) + 1000) {
          return <SilentRenewPage/>;
        } else {
          return this.renderLoginPage();
        }
      }
    } else {
      return this.renderLoginPage();
    }
  }
}

function mapStateToProps(state) {
  return {
    mappEntConfigurationInfo: startupSelectors.getMappEntConfigurationInfo(state),
  };
}

export const LOGIN_FORM_USERNAME_FIELD_NAME = "username";
export const LOGIN_FORM_PASSWORD_FIELD_NAME = "password";
export const LOGIN_FORM_REDIRECT_FIELD_NAME = "redirect";
export const LOGIN_FORM_ID = "loginForm";

export const LoginPage = reduxForm({
  form: LOGIN_FORM_ID,
})(connect(mapStateToProps, null)(withRouter(WithApi(injectIntl(LoginPagePresentation)))));